import type { DienstType } from '@streep/types'

/**
 * Ploegrooster-engine voor 3/4/5-ploegendiensten.
 *
 * Een rooster bestaat uit:
 *  - `pattern`: cyclische array van DayCodes ('V'=Vroeg, 'L'=Laat, 'N'=Nacht,
 *    'D'=Dag, 'X'=vrije dag/rust);
 *  - `cycleDays`: aantal dagen in de cyclus (== pattern.length);
 *  - `startDate`: ankerdatum waarop pattern[0] valt.
 *
 * Voor een gegeven `target`-datum bepalen we het aantal hele dagen tussen
 * `startDate` en `target` (in UTC, om DST-randgevallen te vermijden) en
 * indexeren we het pattern modulo `cycleDays`. Negatieve waarden (target
 * vóór startDate) worden correct teruggewikkeld.
 *
 * `dayCodeToPloegSoort` mapt naar de bestaande `PloegSoort` enum-waarden in
 * de DB (DAG / VROEG / LAAT / NACHT). 'X' valt niet onder een ploegsoort —
 * die dagen worden in de werkbon-recurring-flow geskipt (geen entry).
 */

export type DayCode = 'V' | 'L' | 'N' | 'D' | 'X'

/** Database PloegSoort-waarden voor mapping. */
export type PloegSoortValue = 'DAG' | 'VROEG' | 'LAAT' | 'NACHT'

export interface ShiftRoster {
  /** Cyclische array van DayCodes. Length moet exact gelijk zijn aan cycleDays. */
  pattern: DayCode[]
  /** Ankerdatum waarop pattern[0] geldt. */
  startDate: Date
  /** Aantal dagen in de cyclus (= pattern.length). */
  cycleDays: number
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Aantal hele dagen tussen `from` en `to`, in UTC. Negatief als `to` < `from`.
 *
 * We normaliseren beide datums naar UTC-middernacht zodat zomertijd-overgangen
 * (DST) geen invloed hebben op het resultaat.
 */
function diffDaysUtc(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  const b = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate())
  return Math.round((b - a) / MS_PER_DAY)
}

/**
 * Modulo-helper die altijd een niet-negatief resultaat geeft, ook voor
 * negatieve `n` (zoals JavaScript's `%`-operator dat niet doet).
 *
 * `mod(-1, 10)` -> 9 (i.p.v. -1).
 */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m
}

/**
 * Maandag (UTC-middernacht) van de ISO-week waarin `d` valt.
 * Project-conventie 0=Ma..6=Zo; JS `getUTCDay()` is 0=Zo..6=Za.
 */
function mondayOfWeekUtc(d: Date): Date {
  const projectDay = (d.getUTCDay() + 6) % 7 // 0=Ma .. 6=Zo
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  monday.setUTCDate(monday.getUTCDate() - projectDay)
  return monday
}

/**
 * Lijn het rooster-anker uit voor week-gestructureerde patronen.
 *
 * Een per-week patroon (3-ploegen: 21 dagen = 3 weken) gaat ervan uit dat
 * `pattern[0]` op een MAANDAG valt. Wordt het anker op een andere weekdag
 * gezet (bv. de aanmaakdatum via `todayIso()`), dan schuift de hele fase op en
 * vallen de vrije weekend-'X'-dagen midden in de werkweek — wat de
 * werkbon-generatie als gaten ("3 dagen dan niks dan weer 3 dagen") toont.
 *
 * Daarom snappen we het anker naar de maandag van zijn ISO-week wanneer de
 * cyclus een veelvoud van 7 dagen is. Dag-gebaseerde volcontinue roosters
 * (4-/5-ploegen, cycleDays 10/16) houden hun anker exact — daar is dag-0 een
 * betekenisvolle kalenderdag, geen weekgrens.
 */
export function alignRotationAnchor(startDate: Date, cycleDays: number): Date {
  if (cycleDays > 0 && cycleDays % 7 === 0) {
    return mondayOfWeekUtc(startDate)
  }
  return startDate
}

/**
 * Is dit een PER-WEEK nacht-rooster waarbij de zondag(nacht) de werkweek kan
 * inleiden (2/3-ploegen-structuur)?
 *
 * De zondagnacht-relocatie (`zondagLeidtWerkweekIn` in werkbon-recurring, de
 * server-afleiding in de bulk-create en de modal-auto-detectie) mag alléén
 * vuren op een rooster waarin élke zondag uitsluitend rust ('X') of de
 * inleidende nacht ('N') is. Een 7-voud-cyclus alleen is géén geldig
 * onderscheid: een klant-eigen volcontinu 5-ploegenrooster van 70 dagen
 * (10 weken) is óók een 7-voud, maar daar is de zondag een gewone werkdag
 * (V/L/D) die zijn eigen ma-zo-week afsluit — relocatie zou die zondag een
 * week vooruit etiketteren (Bakker-casus, 2026-08).
 *
 * `pattern[0]` valt op maandag (zie `alignRotationAnchor`), dus zondagen
 * liggen op idx % 7 === 6. Een pattern/cycleDays-mismatch is een ongeldig
 * rooster → false (nooit relocaties op onbetrouwbare weekdag-posities).
 */
export function isPerWeekNachtRooster(pattern: ReadonlyArray<string>, cycleDays: number): boolean {
  if (cycleDays <= 0 || cycleDays % 7 !== 0) return false
  if (pattern.length !== cycleDays) return false
  if (!pattern.includes('N')) return false
  for (let i = 6; i < pattern.length; i += 7) {
    const code = pattern[i]
    if (code !== 'X' && code !== 'N') return false
  }
  return true
}

/**
 * Bepaal de DayCode (V/L/N/D/X) voor een gegeven datum binnen het rooster.
 *
 * Werkt voor data zowel ná als vóór de startDate (modulo wikkelt terug).
 * Gooit een Error als pattern leeg is of cycleDays <= 0.
 */
export function getShiftForDate(roster: ShiftRoster, target: Date): DayCode {
  if (roster.cycleDays <= 0) {
    throw new Error('cycleDays moet > 0 zijn.')
  }
  if (roster.pattern.length !== roster.cycleDays) {
    throw new Error(
      `pattern.length (${roster.pattern.length}) moet gelijk zijn aan cycleDays (${roster.cycleDays}).`,
    )
  }
  const days = diffDaysUtc(roster.startDate, target)
  const idx = mod(days, roster.cycleDays)
  return roster.pattern[idx]
}

/**
 * Map een DayCode naar de DB-`PloegSoort`-waarde, of `null` voor rust-dagen.
 */
export function dayCodeToPloegSoort(code: DayCode): PloegSoortValue | null {
  switch (code) {
    case 'V':
      return 'VROEG'
    case 'L':
      return 'LAAT'
    case 'N':
      return 'NACHT'
    case 'D':
      return 'DAG'
    case 'X':
      return null
  }
}

/**
 * Distinct ploegen uit een rooster-pattern, in volgorde van eerste voorkomen.
 * 'X' (rust/vrije dag) telt niet mee. Onbekende codes worden genegeerd.
 *
 * Spec 07 (ploegrotatie-volgorde-fix): gedeeld tussen de werkbon-create-modal
 * (gekleurde rotatie-badges + submit-volgorde) en de bulk-create route
 * (server-side ploegrotatie-afleiding). Bij een gekoppeld werkschema bepaalt
 * de werkschema-pattern-volgorde de weekrotatie — niet de vaste preset-volgorde.
 *
 * Voorbeeld: `['N','N','L','L','V','V','X','X']` → `['NACHT','LAAT','VROEG']`.
 */
export function distinctPloegen(pattern: ReadonlyArray<string>): PloegSoortValue[] {
  const seen = new Set<PloegSoortValue>()
  const out: PloegSoortValue[] = []
  for (const code of pattern) {
    const ploeg = dayCodeToPloegSoort(code as DayCode)
    if (ploeg && !seen.has(ploeg)) {
      seen.add(ploeg)
      out.push(ploeg)
    }
  }
  return out
}

// FO-348 — `geldendeDienstenInWeek` / `verwachteDienstenInWeek` (+ hun opties)
// wonen sinds batch-40 in `./dienst-slots`, samen met de dienst-slot-lijst waar
// ze uit afgeleid worden. Zo delen de verwacht- en de gepland-kant van de
// dekking gegarandeerd dezelfde dienstdefinitie. Beide blijven via de
// package-root (`@streep/utils`) exporteren; callers wijzigen niet.

/** Een unieke startpositie binnen een rooster-pattern: ploegsoort + offset (index). */
export interface PloegStart {
  ploeg: PloegSoortValue
  offset: number
}

/**
 * Geeft de unieke ploegsoorten terug die als "start van de cyclus" gebruikt
 * kunnen worden, met hun offset (index) in het patroon. 'X' (rustdagen) worden
 * overgeslagen — je start nooit bij een rustdag.
 *
 * Spec 10 (werkbon-ploegrotatie-override): voedt de "Start bij ploeg"-selector
 * in de werkbon-create-modal. De gekozen offset wordt server-side gebruikt om
 * het rooster-pattern te roteren zodat de cyclus bij die ploegsoort begint.
 *
 * Voorbeeld VVLLNNXXXX (5-ploegen, 10 dagen):
 * → [{ ploeg: 'VROEG', offset: 0 }, { ploeg: 'LAAT', offset: 2 },
 *    { ploeg: 'NACHT', offset: 4 }]
 */
export function uniquePloegStarts(pattern: ReadonlyArray<string>): PloegStart[] {
  const seen = new Set<PloegSoortValue>()
  const result: PloegStart[] = []
  for (let i = 0; i < pattern.length; i++) {
    const code = pattern[i]
    if (code === 'X') continue
    const ploeg = dayCodeToPloegSoort(code as DayCode)
    if (ploeg && !seen.has(ploeg)) {
      seen.add(ploeg)
      result.push({ ploeg, offset: i })
    }
  }
  return result
}

/**
 * Roteer een rooster-pattern zodat het bij index `offset` begint:
 * `pattern[offset]` wordt het nieuwe `pattern[0]`. Een offset van 0 (of buiten
 * bereik) laat het pattern ongewijzigd.
 *
 * Spec 10: de bulk-create route past dit toe op het actieve rooster-pattern
 * (werkschema óf modal-`rotation`) op basis van de gekozen `startOffset`, zodat
 * de eerste werkweek bij de gekozen ploegsoort begint.
 */
export function rotatePattern<T>(pattern: ReadonlyArray<T>, offset: number): T[] {
  if (!Number.isInteger(offset) || offset <= 0 || offset >= pattern.length) {
    return [...pattern]
  }
  return [...pattern.slice(offset), ...pattern.slice(0, offset)]
}

/**
 * Rooster-werkschema-flexibiliteit (spec, PO Q20/Q21).
 *
 * Bepaal de effectieve rotatie-offset van een werkbon:
 *  1. Een expliciet op de werkbon opgeslagen `rotationPatternOffset` wint altijd.
 *  2. Anders (null) — legacy-gedrag: leid de offset af uit het werkschema-anker
 *     (`rotationStartDate`), zodat bestaande werkbonnen zonder offset exact
 *     dezelfde fase houden als vóór deze wijziging.
 *  3. Geen anker bekend → offset 0 (begin van het patroon).
 *
 * Het patroon en `cycleDays` komen ALTIJD uit het werkschema (Q21); deze functie
 * raakt die niet aan — ze bepaalt enkel waar de cyclus begint.
 */
export function resolveOffset(
  werkbon: { rotationPatternOffset: number | null; startDatum: Date },
  ws: { rotationStartDate: Date | null; rotationCycleDays: number },
): number {
  // 1. Expliciet opgeslagen offset wint.
  if (werkbon.rotationPatternOffset !== null) {
    return werkbon.rotationPatternOffset
  }
  // 2. Legacy: bereken op basis van het werkschema-anker (oud gedrag).
  if (ws.rotationStartDate && ws.rotationCycleDays > 0) {
    return mod(diffDaysUtc(ws.rotationStartDate, werkbon.startDatum), ws.rotationCycleDays)
  }
  // 3. Geen anker → begin van het patroon.
  return 0
}

/**
 * Bouw een `ShiftRoster` waarvan `pattern[offset]` op `werkbonStart` valt.
 *
 * In plaats van het patroon te roteren (de oude S10-aanpak) verschuiven we het
 * ANKER: `anchorDate = werkbonStart − offset`, daarna uitgelijnd via
 * `alignRotationAnchor`. Zo blijft de weekdag-positie van rust-dagen ('X')
 * intact bij per-week patronen (2/3-ploegen), terwijl de cyclus toch op de
 * gekozen positie begint. Voor volcontinue patronen (4/5-ploegen, `cycleDays`
 * geen 7-voud) is het anker exact `werkbonStart − offset` en levert
 * `getShiftForDate(roster, werkbonStart)` precies `pattern[offset]`.
 */
export function rosterForOffset(
  pattern: DayCode[],
  cycleDays: number,
  werkbonStart: Date,
  offset: number,
): ShiftRoster {
  const anchor = new Date(werkbonStart)
  anchor.setUTCDate(anchor.getUTCDate() - offset)
  return { pattern, startDate: alignRotationAnchor(anchor, cycleDays), cycleDays }
}

export interface RosterPreviewEntry {
  date: Date
  code: DayCode
  ploegSoort: PloegSoortValue | null
}

/**
 * Genereer een vlak overzicht (één entry per dag) voor het bereik
 * [from, to] (beide inclusief). Handig voor mini-kalender-previews.
 */
export function generateRosterPreview(
  roster: ShiftRoster,
  from: Date,
  to: Date,
): RosterPreviewEntry[] {
  const totalDays = diffDaysUtc(from, to)
  if (totalDays < 0) return []
  const out: RosterPreviewEntry[] = []
  const baseUtc = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  for (let i = 0; i <= totalDays; i++) {
    const date = new Date(baseUtc + i * MS_PER_DAY)
    const code = getShiftForDate(roster, date)
    out.push({ date, code, ploegSoort: dayCodeToPloegSoort(code) })
  }
  return out
}

/**
 * Bekende rooster-presets. Elke preset is een complete cyclus.
 *
 * Naamgeving:
 *  - `PLOEGEN_5_STANDAARD`: continudienst 10-daagse cyclus VVLLNNXXXX
 *  - `PLOEGEN_5_VARIANT_A`: continudienst variant DDLNNXVVXX
 *  - `PLOEGEN_4_STANDAARD`: 4-ploegen cyclus DDDDLLLLNNNNXXXX
 *  - `PLOEGEN_3_STANDAARD`: 3-ploegen per-WEEK rotatie V→L→N (21 dagen); de
 *    nacht loopt zo→do (zondagnacht-start), vrijdag-nacht vrij, weekend vrij
 *  - `PLOEGEN_3_VARIANT_VLN`: identiek aan STANDAARD (V→L→N)
 *  - `PLOEGEN_3_VARIANT_NLV`: 3-ploegen per week N→L→V; nacht zo→do, weekend vrij
 */
export interface RosterPreset {
  key: string
  label: string
  pattern: DayCode[]
  cycleDays: number
}

export const ROSTER_PRESET_LIST: ReadonlyArray<RosterPreset> = [
  {
    key: 'PLOEGEN_5_STANDAARD',
    label: '5-ploegen standaard (VVLLNNXXXX)',
    pattern: ['V', 'V', 'L', 'L', 'N', 'N', 'X', 'X', 'X', 'X'],
    cycleDays: 10,
  },
  {
    key: 'PLOEGEN_5_VARIANT_A',
    label: '5-ploegen variant A (DDLNNXVVXX)',
    pattern: ['D', 'D', 'L', 'N', 'N', 'X', 'V', 'V', 'X', 'X'],
    cycleDays: 10,
  },
  {
    key: 'PLOEGEN_4_STANDAARD',
    label: '4-ploegen standaard (DDDDLLLLNNNNXXXX)',
    pattern: ['D', 'D', 'D', 'D', 'L', 'L', 'L', 'L', 'N', 'N', 'N', 'N', 'X', 'X', 'X', 'X'],
    cycleDays: 16,
  },
  {
    key: 'PLOEGEN_3_STANDAARD',
    label: '3-ploegen V→L→N (nacht zo–do, zondagnacht-start, geen vrijdagnacht)',
    // 3-ploegen rouleert per WEEK; 21 dagen = 3 weken, `pattern[0]` valt op
    // maandag (zie alignRotationAnchor). De NACHT-week draait een 5-daagse
    // operatie die op zondagnacht start en vrijdagavond stopt: de inleidende
    // zondagnacht hoort bij de werkweek die hij opent. Daarom is de zondag aan
    // het eind van de laat-week (positie 13) al NACHT en is de vrijdag van de
    // nacht-week vrij. Vroeg/Laat draaien ma–vr; za/zo verder vrij.
    pattern: [
      'V', 'V', 'V', 'V', 'V', 'X', 'X', // week 1: ma-vr Vroeg, za-zo vrij
      'L', 'L', 'L', 'L', 'L', 'X', 'N', // week 2: ma-vr Laat; zondag = inleidende nacht wk3
      'N', 'N', 'N', 'N', 'X', 'X', 'X', // week 3: ma-do Nacht, vr-zo vrij
    ],
    cycleDays: 21,
  },
  {
    key: 'PLOEGEN_3_VARIANT_VLN',
    label: '3-ploegen variant V→L→N (nacht zo–do, geen vrijdagnacht)',
    // Identiek aan PLOEGEN_3_STANDAARD (V→L→N); behouden als aparte key zodat
    // bestaande preset-keuzes blijven werken.
    pattern: [
      'V', 'V', 'V', 'V', 'V', 'X', 'X',
      'L', 'L', 'L', 'L', 'L', 'X', 'N',
      'N', 'N', 'N', 'N', 'X', 'X', 'X',
    ],
    cycleDays: 21,
  },
  {
    key: 'PLOEGEN_3_VARIANT_NLV',
    label: '3-ploegen variant N→L→V (nacht zo–do, zondagnacht-start, geen vrijdagnacht)',
    // Nacht-eerst: de inleidende zondagnacht valt op de zondag VÓÓR de startweek
    // (positie 20, wrapt naar het eind van de cyclus). Nacht-week loopt zo→do,
    // vrijdag vrij; Laat/Vroeg draaien ma–vr.
    pattern: [
      'N', 'N', 'N', 'N', 'X', 'X', 'X', // week 1: ma-do Nacht, vr-zo vrij (zondag-lead = positie 20)
      'L', 'L', 'L', 'L', 'L', 'X', 'X', // week 2: ma-vr Laat, za-zo vrij
      'V', 'V', 'V', 'V', 'V', 'X', 'N', // week 3: ma-vr Vroeg; zondag = inleidende nacht volgende cyclus
    ],
    cycleDays: 21,
  },
  {
    key: 'PLOEGEN_2_STANDAARD',
    label: '2-ploegen standaard (per week: V→L, weekend vrij)',
    pattern: [
      'V', 'V', 'V', 'V', 'V', 'X', 'X',
      'L', 'L', 'L', 'L', 'L', 'X', 'X',
    ],
    cycleDays: 14,
  },
  {
    key: 'PLOEGEN_2_VARIANT_LV',
    label: '2-ploegen variant L→V (per week, weekend vrij)',
    pattern: [
      'L', 'L', 'L', 'L', 'L', 'X', 'X',
      'V', 'V', 'V', 'V', 'V', 'X', 'X',
    ],
    cycleDays: 14,
  },
]

/**
 * Object-vorm van presets — handig voor lookup op key.
 */
export const ROSTER_PRESETS: Record<string, RosterPreset> = Object.fromEntries(
  ROSTER_PRESET_LIST.map((p) => [p.key, p]),
)

export const ROSTER_PRESET_KEYS = ROSTER_PRESET_LIST.map((p) => p.key)

/** Preset-keys per dienstType, in voorkeursvolgorde (eerste = default). */
const PRESETS_PER_DIENSTTYPE: Record<string, string[]> = {
  PLOEGEN_2: ['PLOEGEN_2_STANDAARD', 'PLOEGEN_2_VARIANT_LV'],
  PLOEGEN_3: ['PLOEGEN_3_STANDAARD', 'PLOEGEN_3_VARIANT_VLN', 'PLOEGEN_3_VARIANT_NLV'],
  PLOEGEN_4: ['PLOEGEN_4_STANDAARD'],
  PLOEGEN_5: ['PLOEGEN_5_STANDAARD', 'PLOEGEN_5_VARIANT_A'],
}

/** De roster-presets die bij een ploegendienst horen (leeg voor DAGDIENST/CONSIGNATIE). */
export function rosterPresetsForDienstType(dienstType: DienstType): RosterPreset[] {
  const keys = PRESETS_PER_DIENSTTYPE[dienstType] ?? []
  return keys.map((k) => ROSTER_PRESETS[k]).filter((p): p is RosterPreset => Boolean(p))
}

/** De default (eerste) preset voor een ploegendienst, of null. */
export function defaultRosterPresetForDienstType(dienstType: DienstType): RosterPreset | null {
  return rosterPresetsForDienstType(dienstType)[0] ?? null
}

/**
 * De weekdagen (project-conventie 0=ma … 6=zo) die een preset daadwerkelijk
 * bewerkt. Bron voor de standaard-aangevinkte dagen in de werkbon-create-modal:
 * een dag die het rooster wél draait mag niet ongevinkt blijven, anders dropt
 * `generateRecurringDates` die werkbonnen (de weekdag-array is daar de gate).
 *
 * - **Volcontinu** rooster (`cycleDays` geen 7-voud, bv. 4/5-ploegen 16/10):
 *   draait 24/7 → álle 7 dagen (ma–zo).
 * - **Per-week** rooster (`cycleDays % 7 === 0`, bv. 2/3-ploegen): de unie van de
 *   weekdag-posities (`idx % 7`) met een niet-`'X'`-code over de hele cyclus.
 *   `pattern[0]` valt op maandag (zie `alignRotationAnchor`). Levert voor
 *   3-ploegen-nacht ma–vr + zondag (de inleidende zondagnacht op positie 13/20)
 *   en voor 2-ploegen ma–vr.
 */
export function workedWeekdaysForPreset(preset: RosterPreset): number[] {
  if (preset.cycleDays % 7 !== 0) return [0, 1, 2, 3, 4, 5, 6]
  const worked = new Set<number>()
  preset.pattern.forEach((code, i) => {
    if (code !== 'X') worked.add(i % 7)
  })
  return [...worked].sort((a, b) => a - b)
}
